import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import axios from 'axios';
import {
    currentTokenForSameUser,
    getTokenUserId,
    isSameSession,
    isTokenExpired,
    storeRefreshedTokens
} from '../utils/authTokens';

const AuthContext = createContext();

// Requests that must go out as they are: they either issue tokens or don't need one.
const isAuthEndpoint = (url) => !!url && (
    url.includes('/auth/refresh') ||
    url.includes('/auth/login') ||
    url.includes('/auth/register')
);

// True when the server (or the token's own expiry) says the session is over, as opposed to
// the request failing on the way (offline, timeout, 5xx).
const isSessionRejected = (error) => {
    if (error?.code === 'REFRESH_TOKEN_EXPIRED') return true;
    const status = error?.response?.status;
    return status === 401 || status === 403;
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const orgInactiveAlertShownRef = useRef(false);
    const refreshPromiseRef = useRef(null);
    // Set when a token the server has just issued already looks expired here: this device's
    // clock is wrong, so local expiry checks are skipped and the server's 401 decides instead.
    const clockUntrustedRef = useRef(false);
    const userRef = useRef(null);
    userRef.current = user;

    const withAuthContext = (userProfile, authContext) => {
        if (!userProfile) return null;
        const impersonation = authContext?.impersonation || null;
        const isImpersonating = authContext?.isImpersonating === true;

        return {
            ...userProfile,
            isImpersonating,
            impersonatorRole: impersonation?.impersonatorRole || null,
            impersonatorUserId: impersonation?.impersonatorUserId || null,
            impersonatedOrganizationId: impersonation?.targetOrganizationId || null
        };
    };

    const handleOrganizationInactive = (message) => {
        const inactiveMessage = message || 'Your organization status is Inactive. You cannot proceed.';

        if (!orgInactiveAlertShownRef.current) {
            orgInactiveAlertShownRef.current = true;
            window.alert(inactiveMessage);
        }

        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        setUser(null);
        setError(inactiveMessage);

        if (window.location.pathname !== '/login') {
            window.location.href = '/login';
        }
    };

    // Refresh auth token function. Concurrent callers share one request, so a page that
    // fires several calls with an expired token rotates the refresh token only once.
    const refreshAuthToken = () => {
        if (!refreshPromiseRef.current) {
            refreshPromiseRef.current = requestTokenRefresh().finally(() => {
                refreshPromiseRef.current = null;
            });
        }
        return refreshPromiseRef.current;
    };

    const requestTokenRefresh = async () => {
        const refreshToken = localStorage.getItem('refreshToken');
        if (!refreshToken) {
            throw new Error('No refresh token available');
        }

        // The session can change while the refresh is in flight (logout, a different login,
        // another tab). Then its result belongs to the old session and must not be used.
        const resolveChangedSession = () => {
            const sameUserToken = currentTokenForSameUser(refreshToken);
            if (sameUserToken) return sameUserToken;
            const staleError = new Error('Session changed during token refresh');
            staleError.code = 'SESSION_CHANGED';
            throw staleError;
        };

        try {
            if (!clockUntrustedRef.current && isTokenExpired(refreshToken)) {
                // The session has run out. The server would only refuse this refresh token
                // (and log it as an error), so go straight to the login below.
                const expiredError = new Error('Refresh token expired');
                expiredError.code = 'REFRESH_TOKEN_EXPIRED';
                throw expiredError;
            }

            const response = await axios.post('/api/auth/refresh', {
                refreshToken: refreshToken
            });

            const { tokens } = response.data;
            if (isTokenExpired(tokens?.accessToken)) {
                clockUntrustedRef.current = true;
            }
            if (!storeRefreshedTokens(refreshToken, tokens)) {
                return resolveChangedSession();
            }

            return tokens.accessToken;
        } catch (error) {
            if (error?.code === 'SESSION_CHANGED') throw error;
            // Don't clear or redirect a session this refresh no longer belongs to.
            if (!isSameSession(refreshToken)) {
                return resolveChangedSession();
            }
            // A network blip or server error says nothing about the session. Keep it, so a job
            // seeker waiting in a queue or sitting in a call isn't logged out; the next request
            // tries the refresh again.
            if (!isSessionRejected(error)) {
                throw error;
            }
            // The server refused the refresh token: clear auth and redirect to login
            localStorage.removeItem('token');
            localStorage.removeItem('refreshToken');
            setUser(null);
            window.location.href = '/login';
            throw error;
        }
    };

    // Set up axios interceptor for automatic token refresh
    useEffect(() => {
        let isRefreshingToken = false;
        let failedQueue = [];

        const processQueue = (error, token = null) => {
            failedQueue.forEach(prom => {
                if (error) {
                    prom.reject(error);
                } else {
                    prom.resolve(token);
                }
            });
            failedQueue = [];
        };

        // Request interceptor - add token to requests. An access token that has already
        // expired is refreshed first rather than sent to be refused.
        const requestInterceptor = axios.interceptors.request.use(
            async (config) => {
                let token = localStorage.getItem('token');
                if (token && !clockUntrustedRef.current && !isAuthEndpoint(config.url)
                    && isTokenExpired(token)) {
                    token = await refreshAuthToken();
                }
                if (token) {
                    config.headers.Authorization = `Bearer ${token}`;
                }
                return config;
            },
            (error) => {
                return Promise.reject(error);
            }
        );

        // Response interceptor - handle 401 errors and refresh token
        const responseInterceptor = axios.interceptors.response.use(
            (response) => {
                return response;
            },
            async (error) => {
                const originalRequest = error.config;

                // No config means the request never went out (the refresh in the request
                // interceptor failed). Don't intercept login or refresh token requests either.
                if (!originalRequest || isAuthEndpoint(originalRequest.url)) {
                    return Promise.reject(error);
                }

                if (
                    error.response?.status === 403 &&
                    error.response?.data?.error === 'OrganizationInactive'
                ) {
                    handleOrganizationInactive(error.response?.data?.message);
                    return Promise.reject(error);
                }

                // If error is 401 and we haven't tried to refresh yet
                if (error.response?.status === 401 && !originalRequest._retry) {
                    if (isRefreshingToken) {
                        // If already refreshing, queue this request
                        return new Promise((resolve, reject) => {
                            failedQueue.push({ resolve, reject });
                        })
                            .then(token => {
                                originalRequest.headers.Authorization = `Bearer ${token}`;
                                return axios(originalRequest);
                            })
                            .catch(err => {
                                return Promise.reject(err);
                            });
                    }

                    originalRequest._retry = true;
                    isRefreshingToken = true;

                    try {
                        const newToken = await refreshAuthToken();
                        processQueue(null, newToken);
                        originalRequest.headers.Authorization = `Bearer ${newToken}`;
                        return axios(originalRequest);
                    } catch (refreshError) {
                        processQueue(refreshError, null);
                        return Promise.reject(refreshError);
                    } finally {
                        isRefreshingToken = false;
                    }
                }

                return Promise.reject(error);
            }
        );

        // Cleanup interceptors on unmount
        return () => {
            axios.interceptors.request.eject(requestInterceptor);
            axios.interceptors.response.eject(responseInterceptor);
        };
    }, []);

    // Check for existing token on mount
    useEffect(() => {
        const token = localStorage.getItem('token');
        if (token) {
            // Verify token with backend
            verifyToken(token);
        } else {
            setLoading(false);
        }
    }, []);

    // Logging out in one tab clears the shared token. Log this tab out too, so its socket
    // closes and the user stops showing as online (Sprint 7, Support Analytics).
    // Logging in as someone else in another tab replaces the shared token. Follow it, so this
    // tab doesn't keep showing the previous user while sending the new user's token.
    useEffect(() => {
        const handleStorage = (event) => {
            const tokenCleared = (event.key === 'token' && !event.newValue) || event.key === null;
            if (tokenCleared && !localStorage.getItem('token')) {
                setUser(null);
                return;
            }
            if (event.key === 'token' && event.newValue) {
                const newUserId = getTokenUserId(event.newValue);
                const currentUserId = userRef.current?._id ? String(userRef.current._id) : null;
                if (newUserId && newUserId !== currentUserId) {
                    syncUserFromToken(event.newValue);
                }
            }
        };
        window.addEventListener('storage', handleStorage);
        return () => window.removeEventListener('storage', handleStorage);
    }, []);

    const verifyToken = async (token) => {
        try {
            const response = await axios.get('/api/auth/me', {
                headers: { Authorization: `Bearer ${token}` }
            });
            setUser(withAuthContext(response.data.user, response.data.authContext));
        } catch (error) {
            console.error('Token verification failed:', error);
            // Only forget the tokens when the server rejected them. If the page loaded during a
            // network blip, keep them so a reload signs the user straight back in.
            if (isSessionRejected(error)) {
                localStorage.removeItem('token');
                localStorage.removeItem('refreshToken');
            }
            setUser(null);
        } finally {
            setLoading(false);
        }
    };

    // Load the user for a token another tab stored. Leaves storage alone on failure, since the
    // token belongs to the other tab's session too.
    const syncUserFromToken = async (token) => {
        try {
            const response = await axios.get('/api/auth/me', {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (localStorage.getItem('token') === token) {
                setUser(withAuthContext(response.data.user, response.data.authContext));
            }
        } catch (error) {
            console.error('Failed to sync user from another tab:', error);
        }
    };

    // Update current user's profile
    const updateProfile = async (profileData) => {
        try {
            setError(null);
            const token = localStorage.getItem('token');
            const response = await axios.put('/api/auth/profile', profileData, {
                headers: { Authorization: `Bearer ${token}` }
            });
            // Update local user with server's canonical data
            setUser(prev => withAuthContext(response.data.user, {
                isImpersonating: prev?.isImpersonating === true,
                impersonation: prev?.isImpersonating ? {
                    impersonatorRole: prev?.impersonatorRole,
                    impersonatorUserId: prev?.impersonatorUserId,
                    targetOrganizationId: prev?.impersonatedOrganizationId
                } : null
            }));
            return { success: true, user: response.data.user };
        } catch (error) {
            const errorMessage = error.response?.data?.message || 'Profile update failed';
            setError(errorMessage);
            return { success: false, error: errorMessage };
        }
    };

    // Change password
    const changePassword = async (currentPassword, newPassword) => {
        try {
            setError(null);
            const token = localStorage.getItem('token');
            const response = await axios.post('/api/auth/change-password', { currentPassword, newPassword }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            return { success: true, message: response.data?.message || 'Password changed successfully' };
        } catch (error) {
            const errorMessage = error.response?.data?.message || 'Password change failed';
            setError(errorMessage);
            return { success: false, error: errorMessage };
        }
    };

    const login = async (email, password, loginType) => {
        try {
            setError(null);
            const response = await axios.post('/api/auth/login', {
                email,
                password,
                loginType
            });

            const { tokens, user } = response.data;
            localStorage.setItem('token', tokens.accessToken);
            localStorage.setItem('refreshToken', tokens.refreshToken);
            setUser(withAuthContext(user, response.data.authContext));
            return { success: true };
        } catch (error) {            
            const status = error.response?.status;
            const data = error.response?.data || {};
            let errorMessage;
            let errorCode;

            if (status === undefined) {
                // Network error or no response from server
                errorMessage = 'Unable to connect. Please check your connection and try again.';
            } else if (status === 401) {
                // Use the specific error message from the backend
                if (data?.error === 'Email not found') {
                    errorMessage = data?.message || 'User does not exist. Please enter a valid email.';
                } else if (data?.error === 'Incorrect password') {
                    errorMessage = data?.message || 'Incorrect password.';
                } else if (data?.error === 'Account deactivated') {
                    errorMessage = data?.message || 'Your account has been deactivated. Please contact support.';
                } else {
                    errorMessage = data?.message || 'Invalid email or password';
                }
            } else if (status === 403 && data?.error === 'Email not verified') {
                errorMessage = data?.message || 'Please verify your email before logging in.';
                errorCode = 'EMAIL_NOT_VERIFIED';
            } else if (status === 403 && (data?.error === 'Role not allowed' || /Please use the (Company|Job) Seeker login/i.test(data?.message || ''))) {
                errorMessage = data?.message || 'This account type is not allowed on the selected login. Try the other login tab.';
            } else if (status === 403 && data?.error === 'OrganizationInactive') {
                errorMessage = data?.message || 'Your organization status is Inactive. You cannot proceed.';
            } else if (status === 403 || data?.error === 'Account deactivated' || /deactivated/i.test(data?.message || '')) {
                errorMessage = 'Your account has been deactivated. Please contact support.';
            } else {
                errorMessage = data?.message || 'Login failed';
            }
            
            setError(errorMessage);
            return { success: false, error: errorMessage, errorCode };
        }
    };

    const register = async (userData) => {
        try {
            setError(null);
            await axios.post('/api/auth/register', userData);
            return { success: true };
        } catch (error) {
            const errorMessage = error.response?.data?.message || 'Registration failed';
            setError(errorMessage);
            return { success: false, error: errorMessage };
        }
    };

    const startImpersonation = async (organizationId) => {
        try {
            setError(null);
            const response = await axios.post('/api/auth/impersonate/start', { organizationId });
            const { tokens, user: impersonatedUser, authContext } = response.data || {};

            if (!tokens?.accessToken || !tokens?.refreshToken || !impersonatedUser) {
                throw new Error('Invalid impersonation response');
            }

            localStorage.setItem('token', tokens.accessToken);
            localStorage.setItem('refreshToken', tokens.refreshToken);
            setUser(withAuthContext(impersonatedUser, authContext));
            return { success: true };
        } catch (error) {
            const errorMessage = error.response?.data?.message || 'Failed to access organization admin panel';
            setError(errorMessage);
            return { success: false, error: errorMessage };
        }
    };

    const stopImpersonation = async () => {
        try {
            setError(null);
            const response = await axios.post('/api/auth/impersonate/stop');
            const { tokens, user: restoredUser, authContext } = response.data || {};

            if (!tokens?.accessToken || !tokens?.refreshToken || !restoredUser) {
                throw new Error('Invalid stop impersonation response');
            }

            localStorage.setItem('token', tokens.accessToken);
            localStorage.setItem('refreshToken', tokens.refreshToken);
            setUser(withAuthContext(restoredUser, authContext));
            return { success: true };
        } catch (error) {
            const errorMessage = error.response?.data?.message || 'Failed to exit impersonation';
            setError(errorMessage);
            return { success: false, error: errorMessage };
        }
    };

    const logout = () => {
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        setUser(null);
        setError(null);
    };

    const value = {
        user,
        loading,
        error,
        login,
        register,
        logout,
        startImpersonation,
        stopImpersonation,
        updateProfile,
        changePassword,
        refreshAuthToken,
        isAuthenticated: !!user
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};
